// src/screens/report/screen/vision2028/api/discipleTypes.ts
import axios from 'axios';
import { Disciple } from '../types/disciple';

export interface Counts {
  disciplesCount: number;
  ministryTeamsCount: number;
}

/**
 * 제자종류 목록을 DB에서 가져온다.
 * @returns {Promise<Array<{value: string, label: string}>>} 제자종류 옵션 배열
 */
export const getDiscipleTypes = async () => {
  let result = [];

  try {
    const response = await axios.get(`${process.env.REACT_APP_YCMS_BACKEND_SERVER_URL}/projects/vision2028/disciple-types`, {
      withCredentials: true,
    }); // ycms-backend-server
    result = response.data || [];
  } catch (e) {
    console.error('제자종류 데이터를 가져오는 중 오류 발생:', e.message);
  }

  return result;
};

/**
 * 제자종류를 DB에 추가한다.
 * @param {Object} discipleType 추가할 제자종류 데이터
 * @returns {Promise<Object>} 추가된 제자종류 데이터
 */
export const createDiscipleType = async (discipleType: { departmentId: number; displayName: string; description: string; target?: number }) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_YCMS_BACKEND_SERVER_URL}/projects/vision2028/disciple-types`, discipleType, {
      withCredentials: true,
    });
    return response.data;
  } catch (e) {
    console.error('제자종류 데이터를 추가하는 중 오류 발생:', e.message);
    throw e;
  }
};

/**
 * 제자종류를 DB에서 수정한다.
 * @param {Object} discipleType 수정할 제자종류 데이터
 * @returns {Promise<Object>} 수정된 제자종류 데이터
 */
export const updateDiscipleType = async (discipleType: { id: number; departmentId: number; displayName: string; description: string; target?: number }) => {
  try {
    const response = await axios.put(
      `${process.env.REACT_APP_YCMS_BACKEND_SERVER_URL}/projects/vision2028/disciple-types/${discipleType.id}`,
      {
        departmentId: discipleType.departmentId,
        displayName: discipleType.displayName,
        description: discipleType.description,
        target: discipleType.target,
      },
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (e) {
    console.error('제자종류 데이터를 수정하는 중 오류 발생:', e.message);
    throw e;
  }
};

/**
 * 제자종류를 DB에서 삭제한다.
 * @param {number} id 삭제할 제자종류의 id
 * @returns {Promise<void>}
 */
export const deleteDiscipleType = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${process.env.REACT_APP_YCMS_BACKEND_SERVER_URL}/projects/vision2028/disciple-types/${id}`, {
      withCredentials: true,
    });
  } catch (e) {
    console.error('제자종류 데이터를 삭제하는 중 오류 발생:', e.message);
    throw e;
  }
};

/**
 * 제자 리스트를 DB에서 가져온다.
 * @returns {Promise<Disciple[]>} 제자 배열
 */
export const getDisciples = async (): Promise<Disciple[]> => {
  let result: Disciple[] = [];

  try {
    const response = await axios.get(`${process.env.REACT_APP_YCMS_BACKEND_SERVER_URL}/projects/vision2028/disciples`, {
      withCredentials: true,
    });
    result = response.data || [];
  } catch (e) {
    console.error('제자 데이터를 가져오는 중 오류 발생:', e.message);
  }

  return result;
};

/**
 * 제자를 DB에 추가한다.
 * @param {Disciple} disciple 추가할 제자 데이터
 * @returns {Promise<Disciple>} 추가된 제자 데이터
 */
export const createDisciple = async (disciple: Disciple): Promise<Disciple> => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_YCMS_BACKEND_SERVER_URL}/projects/vision2028/disciples`, disciple, {
      withCredentials: true,
    });
    return response.data;
  } catch (e) {
    console.error('제자 데이터를 추가하는 중 오류 발생:', e.message);
    throw e;
  }
};

/**
 * 제자를 DB에서 삭제한다.
 * @param {number} id 삭제할 제자의 id
 * @returns {Promise<void>}
 */
export const deleteDisciple = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${process.env.REACT_APP_YCMS_BACKEND_SERVER_URL}/projects/vision2028/disciples/${id}`, {
      withCredentials: true,
    });
  } catch (e) {
    console.error('제자 데이터를 삭제하는 중 오류 발생:', e.message);
    throw e;
  }
};

/**
 * Vision2028 제자와 사역팀 카운트를 DB에서 가져온다.
 * @returns {Promise<Counts>} 제자 카운트와 사역팀 카운트 오브젝트
 */
export const getCounts = async (): Promise<Counts> => {
  let result: Counts = {
    disciplesCount: 0,
    ministryTeamsCount: 0,
  };

  try {
    const response = await axios.get(`${process.env.REACT_APP_YCMS_BACKEND_SERVER_URL}/projects/vision2028/counts`, {
      withCredentials: true,
    });
    result = response.data || result;
  } catch (e) {
    console.error('Vision2028 카운트를 가져오는 중 오류 발생:', e.message);
  }

  return result;
};
